import * as Yup from 'yup';

// Route constants
export const CLIENT_LIST_ROUTE = '/client-list';

// Dropdown Options (matching backend validation)
export const LEGAL_STRUCTURE_OPTIONS = [
	{ value: 'Company', label: 'Company' },
	{ value: 'LLP', label: 'LLP' },
	{ value: 'Individual', label: 'Individual' },
	// { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
	// { value: 'Other', label: 'Other' },
];

export const PRIMARY_COST_CENTER_OPTIONS = [
	{ value: 'Logistics', label: 'Logistics' },
	{ value: 'Distribution', label: 'Distribution' },
	// { value: 'Sales', label: 'Sales' },
	// { value: 'Finance', label: 'Finance' },
	// { value: 'HR', label: 'HR' },
	// { value: 'IT', label: 'IT' },
	// { value: 'Other', label: 'Other' },
];

export const GST_TYPE_OPTIONS = [
	{ value: 'Unregistered', label: 'Unregistered' },
	{ value: 'Registered', label: 'Registered' },
];

export const ADDITIONAL_DOCUMENT_TYPE_OPTIONS = [
	{ value: 'TAN', label: 'TAN' },
	{ value: 'CIN', label: 'CIN' },
];

// Empty Objects
export const EMPTY_BILLING_ADDRESS = {
	address_line1: '',
	address_line2: '',
	city: '',
	state: '',
	pincode: '',
	zone: '',
};

export const EMPTY_ADDITIONAL_DOCUMENT = {
	document_type: undefined,
	document_number: '',
	document_file_key: '',
	file_uploaded: false,
};

// Dummy data constants
const DUMMY_COMPANY_NAME = 'Intelligent Retail PVT LTD';

// Dummy data for edit mode (matches the list row)
export const DUMMY_CLIENT_DATA = {
	id: 1,
	client_legal_name: 'CLN-1',
	company_name: 'Stark-logistics',
	legal_structure: 'Company', // Must match backend enum
	primary_cost_center: 'Logistics', // Must match backend enum
	primary_poc_name: 'My Logi',
	primary_poc_contact: '76543456782',
	finance_poc_name: DUMMY_COMPANY_NAME,
	finance_poc_contact: '98176253221',
	email_id: 'example@gmail.com',
	head_office_address: DUMMY_COMPANY_NAME,
	pincode: '517408',
	zone: 'South',
	city: 'Bangalore',
	state: 'Karnataka',
	billing_addresses: [
		{
			address_line1: DUMMY_COMPANY_NAME,
			address_line2: DUMMY_COMPANY_NAME,
			city: 'Bangalore',
			state: 'Karnataka',
			pincode: '517408',
			zone: 'South',
		},
	],
	pan_number: 'VCOCT1111A',
	gst_type: 'unregistered', // Must match GST_TYPE_OPTIONS values (lowercase)
	gst_number: '22AAAAA1234A1Z5',
	credit_period: 15,
	is_active: true,
};

// Validation Schemas
export const BasicInfoSchema = Yup.object({
	client_legal_name: Yup.string()
		.required('Client Legal Name is required')
		.min(3, 'Client Legal Name must be at least 3 characters')
		.max(100, 'Client Legal Name must not exceed 100 characters')
		.matches(
			/^[a-zA-Z0-9\s\-.]+$/,
			'Client Legal Name can only contain letters, numbers, spaces, hyphens, and dots (no underscores)'
		),
	company_name: Yup.string()
		.required('Company Name is required')
		.min(3, 'Company Name must be at least 3 characters')
		.max(100, 'Company Name must not exceed 100 characters'),
	legal_structure: Yup.string().required('Legal Structure is required'),
	primary_cost_center: Yup.string().required('Primary Cost Center is required'),
	primary_poc_name: Yup.string()
		.required('Primary PoC Name is required')
		.min(2, 'Primary PoC Name must be at least 2 characters')
		.max(50, 'Primary PoC Name must not exceed 50 characters'),
	primary_poc_contact: Yup.string()
		.required('Primary PoC Contact is required')
		.matches(/^[6-9][0-9]{9}$/, 'Mobile number is invalid'),
	finance_poc_name: Yup.string()
		.min(2, 'Finance PoC Name must be at least 2 characters')
		.max(50, 'Finance PoC Name must not exceed 50 characters'),
	finance_poc_contact: Yup.string().test(
		'valid-mobile',
		'Mobile number is invalid',
		(value) => !value || /^[6-9][0-9]{9}$/.test(value)
	),
	email_id: Yup.string().required('Email ID is required').email('Enter valid email'),
	head_office_address: Yup.string()
		.required('Head Office Address is required')
		.min(5, 'Head Office Address must be at least 5 characters')
		.max(200, 'Head Office Address must not exceed 200 characters'),
	pincode: Yup.string()
		.required('Pincode is required')
		.matches(/^[0-9]{6}$/, 'Enter valid 6 digit pincode'),
	zone: Yup.string(),
	city: Yup.string(),
	state: Yup.string(),
});

export const BusinessInfoSchema = Yup.object({
	billing_addresses: Yup.array()
		.min(1, 'At least one billing address is required')
		.of(
			Yup.object({
				address_line1: Yup.string()
					.required('Address Line 1 is required')
					.min(5, 'Address Line 1 must be at least 5 characters')
					.max(200, 'Address Line 1 must not exceed 200 characters'),
				address_line2: Yup.string().max(
					200,
					'Address Line 2 must not exceed 200 characters'
				),
				city: Yup.string(),
				state: Yup.string(),
				pincode: Yup.string()
					.required('Pincode is required')
					.matches(/^[0-9]{6}$/, 'Enter valid 6 digit pincode'),
				zone: Yup.string(),
			})
		),
	pan_number: Yup.string()
		.required('PAN Number is required')
		.matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Enter valid PAN number (e.g., ABCDE1234F)'),
	gst_type: Yup.string().required('GST Type is required'),
	gst_number: Yup.string().when('gst_type', {
		is: 'Registered',
		then: (schema) =>
			schema
				.required('GST Number is required for Registered GST Type')
				.matches(
					/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
					'Enter valid GST number (e.g., 22AAAAA0000A1Z5)'
				),
		otherwise: (schema) => schema.notRequired(),
	}),
	credit_period: Yup.number()
		.required('Credit Period is required')
		.min(1, 'Credit Period must be at least 1 day')
		.max(365, 'Credit Period must not exceed 365 days')
		.integer('Credit Period must be a whole number'),
	crf_number: Yup.string()
		.required('CRF Number is required')
		.min(2, 'CRF Number must be at least 2 characters')
		.max(50, 'CRF Number must not exceed 50 characters'),
	// File upload validation fields
	pan_file_uploaded: Yup.boolean()
		.required('PAN document is required')
		.oneOf([true], 'Please upload PAN document'),
	gst_file_uploaded: Yup.boolean().when('gst_type', {
		is: 'Registered',
		then: (schema) =>
			schema.required('GST document is required').oneOf([true], 'Please upload GST document'),
		otherwise: (schema) => schema,
	}),
	crf_file_uploaded: Yup.boolean()
		.required('CRF document is required')
		.oneOf([true], 'Please upload CRF document'),
	// Additional documents array (optional, but if added, fields are required)
	additional_documents: Yup.array().of(
		Yup.object({
			document_type: Yup.string().required('Document Type is required'),
			document_number: Yup.string()
				.required('Document Number is required')
				.test('tan-validation', 'Invalid TAN', function (value) {
					const { document_type } = this.parent;
					if (document_type === 'TAN') {
						if (!value) return true; // Required check is handled above
						return (
							value.length === 10 &&
							/^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(value.toUpperCase())
						);
					}
					return true;
				})
				.test('cin-validation', 'Invalid CIN', function (value) {
					const { document_type } = this.parent;
					if (document_type === 'CIN') {
						if (!value) return true; // Required check is handled above
						return (
							value.length === 21 &&
							/^[U][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(
								value.toUpperCase()
							)
						);
					}
					return true;
				}),
			document_file_key: Yup.string(),
			file_uploaded: Yup.boolean()
				.required('Document file is required')
				.oneOf([true], 'Please upload document file'),
		})
	),
});
